import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Container, Row, Col } from 'react-grid-system';
import ProjectHomeCard from './components/ProjectHomeCard';
import FeaturedCaseStudyCard from './components/FeaturedCaseStudyCard';
import { loadGLTF } from './gltfLoader';
import ProjectModel from './models/ProjectModel';
import { connect } from "react-redux";
import setCoverPresent, { setLightNav } from './redux/actions';
import './App.css';

const COMMISSIONS_BG = '#085C34';

const row1Images = ProjectModel.FigmaPractice.images.map(src => ({
  src,
  caption: ProjectModel.FigmaPractice.client,
  title: ProjectModel.FigmaPractice.title,
  credits: ProjectModel.FigmaPractice.credits,
  imageMakerName: ProjectModel.FigmaPractice.imageMakerName,
  imageMakerLink: ProjectModel.FigmaPractice.imageMakerLink,
}));

const row2Images = ProjectModel.FigmaPrompt.images.map(src => ({
  src,
  caption: ProjectModel.FigmaPrompt.client,
  title: ProjectModel.FigmaPrompt.title,
  credits: ProjectModel.FigmaPrompt.credits,
  imageMakerName: ProjectModel.FigmaPrompt.imageMakerName,
  imageMakerLink: ProjectModel.FigmaPrompt.imageMakerLink,
}));

function Slideshow({ images }) {
  const [idx, setIdx] = useState(0);
  const current = images[idx];

  const handleClick = (e) => {
    if (images.length <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x > rect.width / 2) {
      setIdx(i => (i + 1) % images.length);
    } else {
      setIdx(i => (i - 1 + images.length) % images.length);
    }
  };

  return (
    <div>
      <div style={{ position: 'relative', cursor: images.length > 1 ? 'pointer' : 'default' }} onClick={handleClick}>
        <img src={current.src} alt="" style={{ display: 'block', width: '100%' }} />
      </div>
      <div className="caption" style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span>{current.caption}, <span style={{ textTransform: 'none' }}>{current.title}</span></span>
        {images.length > 1 && <span style={{ textTransform: 'none' }}>{idx + 1} of {images.length}</span>}
      </div>
      {current.credits && (
        <div className="title">
          <span className="slideshow-credits" style={{ display: 'block', marginTop: '0.6em', textTransform: 'none', fontSize: 'inherit', fontWeight: 'inherit', maxWidth: '60ch' }} dangerouslySetInnerHTML={{ __html: current.credits }} />
        </div>
      )}
    </div>
  );
}

function Homepage({dispatch}) {
  const csSectionRef = useRef(null);
  const commissionsSectionRef = useRef(null);

  useLayoutEffect(() => {
    const app = document.querySelector('.App');
    document.documentElement.classList.add('page-grey');
    document.documentElement.style.backgroundColor = '#e0e0e0';
    document.body.style.backgroundColor = '#e0e0e0';
    if (app) app.style.backgroundColor = '#e0e0e0';
    return () => {
      document.documentElement.classList.remove('page-grey');
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      if (app) app.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    dispatch(setCoverPresent('no'));
    // Preload GLTF models so they're cached before cards mount
    loadGLTF('/models/30-MariaChimishkyan-Figma-MW.glb');
    loadGLTF('/models/28-MariaChimishkyan-Figma-Config25.glb');
    loadGLTF('/models/MariaChimishkyan-Google-TSOS.glb');

    const app = document.querySelector('.App');
    let rafId = null;
    let lastBg = '#e0e0e0';
    const HYSTERESIS = 40; // px deadband to prevent flickering at the boundary

    const applyBackground = () => {
      if (!csSectionRef.current) return;

      const csBottomAbs = csSectionRef.current.getBoundingClientRect().bottom + window.scrollY;
      const darkEl = document.querySelector('.selected-work-section');
      const darkTopAbs = darkEl
        ? darkEl.getBoundingClientRect().top + window.scrollY
        : csBottomAbs;
      const navH = document.querySelector('.nav')?.offsetHeight || 70;

      const commissionsEl = commissionsSectionRef.current;
      const commissionsTopAbs = commissionsEl
        ? commissionsEl.getBoundingClientRect().top + window.scrollY
        : Infinity;

      // Body background: three zones
      const threshold = csBottomAbs - window.innerHeight / 2;
      let inDark = lastBg === '#000'
        ? window.scrollY > threshold - HYSTERESIS
        : window.scrollY > threshold + HYSTERESIS;
      const commissionsThreshold = commissionsTopAbs - window.innerHeight / 2;
      const inCommissions = commissionsTopAbs > window.innerHeight && (lastBg === COMMISSIONS_BG
        ? window.scrollY > commissionsThreshold - HYSTERESIS
        : window.scrollY > commissionsThreshold + HYSTERESIS);

      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const atBottom = docHeight > window.innerHeight * 2 && scrollBottom > docHeight - 300;

      if (atBottom || inCommissions) lastBg = COMMISSIONS_BG;
      else if (inDark) lastBg = '#000';
      else lastBg = '#e0e0e0';

      // Nav color: based on which section the nav bar overlaps
      const navY = window.scrollY + 50;
      let navColor;
      if (navY >= commissionsTopAbs) navColor = COMMISSIONS_BG;
      else if (navY >= darkTopAbs) navColor = '#000000';
      else navColor = '#e0e0e0';

      document.documentElement.style.backgroundColor = lastBg;
      document.body.style.backgroundColor = lastBg;
      if (app) app.style.backgroundColor = lastBg;
      dispatch(setLightNav(navColor));
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        applyBackground();
      });
    };

    applyBackground();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
      dispatch(setLightNav(false));
    };
  }, []);

  return (
    <div className="wrapper">

      {/* ── Featured Case Studies ── */}
      <div className="featured-cs-section" ref={csSectionRef}>
        <div className="inner-wrapper">
        <Container fluid className="page-pad">
          <Row>
            <Col sm={6}>
              <FeaturedCaseStudyCard projectModel={ProjectModel.Config2025} mountIndex={0} />
            </Col>
            <Col sm={6}>
              <FeaturedCaseStudyCard projectModel={ProjectModel.MakerWeek} mountIndex={1} />
            </Col>
            <Col sm={6}>
              <FeaturedCaseStudyCard projectModel={ProjectModel.ShapeOfSense} mountIndex={2} />
            </Col>
            <Col sm={6}>
              <FeaturedCaseStudyCard projectModel={ProjectModel.YoungGuns} mountIndex={3} />
            </Col>
          </Row>
        </Container>
        </div>
      </div>

      {/* ── Selected Work ────────────────────────── */}
      <div className="inner-wrapper" style={{ backgroundColor: '#000', paddingBottom: 140 }}>
        <Container fluid className="page-pad">
          <div className="selected-work-section">
          <div className="selected-work-label">Selected Work</div>

          <div className="selected-work-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', columnGap: 12, alignItems: 'start' }}>
            <ProjectHomeCard projectModel={ProjectModel.NYTimesRightToBeauty}/>
            <ProjectHomeCard projectModel={ProjectModel.NewYorker_BitterPill}/>
            <ProjectHomeCard projectModel={ProjectModel.AdidasStateSneakers}/>
            <ProjectHomeCard projectModel={ProjectModel.CashApp}/>

            <ProjectHomeCard projectModel={ProjectModel.HighSnobiety}/>
            <ProjectHomeCard projectModel={ProjectModel.LATimesClimateChange}/>
            <ProjectHomeCard projectModel={ProjectModel.NewYorker_MDMA}/>
            <ProjectHomeCard projectModel={ProjectModel.NYTimes}/>

            <ProjectHomeCard projectModel={ProjectModel.TheVergeOfflineActivities}/>
            <ProjectHomeCard projectModel={ProjectModel.AppleArtText}/>
            <ProjectHomeCard projectModel={ProjectModel.NewYorker}/>
            <ProjectHomeCard projectModel={ProjectModel.Atlantic}/>

            <ProjectHomeCard projectModel={ProjectModel.BloombergBusinessweekRealEstate}/>
            <ProjectHomeCard projectModel={ProjectModel.Wired}/>
            <ProjectHomeCard projectModel={ProjectModel.Apple}/>
            <ProjectHomeCard projectModel={ProjectModel.BloombergBusinessweekJunk}/>
            <ProjectHomeCard projectModel={ProjectModel.YoungGuns}/>
            <ProjectHomeCard projectModel={ProjectModel.BloombergBusinessweekSpotify}/>
            <ProjectHomeCard projectModel={ProjectModel.TheBaffler}/>

            <ProjectHomeCard projectModel={ProjectModel.Spotify}/>
            <ProjectHomeCard projectModel={ProjectModel.RBMA}/>
            <ProjectHomeCard projectModel={ProjectModel.Nike}/>
            <ProjectHomeCard projectModel={ProjectModel.BandcampExperimental}/>
           

            <ProjectHomeCard projectModel={ProjectModel.SpaceNinety8}/>
            <ProjectHomeCard projectModel={ProjectModel.BloombergBusinessweekCover}/>
            <ProjectHomeCard projectModel={ProjectModel.LuxMagazine}/>
            <ProjectHomeCard projectModel={ProjectModel.Bandcamp}/>
          
            

            <ProjectHomeCard projectModel={ProjectModel.BloombergGreen}/>
            
            <div/>

            <div/>
            
          </div>
          </div>
        </Container>
      </div>

      {/* ── Commissions ── */}
      <div className="inner-wrapper commissions-section" style={{ backgroundColor: COMMISSIONS_BG, paddingBottom: 140 }} ref={commissionsSectionRef}>
        <Container fluid className="page-pad">

          <div className="selected-work-label">Editorial Art Direction</div>

          <Row>
            <Col sm={6} className="editorial-item">
              <Slideshow images={row1Images} />
            </Col>
            <Col sm={6} className="editorial-item">
              <Slideshow images={row2Images} />
            </Col>
          </Row>

          <div className="cs-section-sep">
          <Row>
            <Col sm={3} className="editorial-item">
              <img src={ProjectModel.FigmaVersionControl.src} alt="" style={{ display: 'block', width: '100%' }} />
              <div className="caption">{ProjectModel.FigmaVersionControl.client}</div>
              <div className="title">{ProjectModel.FigmaVersionControl.title}{ProjectModel.FigmaVersionControl.imageMakerName && <span style={{ display: 'block' }}><a href={ProjectModel.FigmaVersionControl.imageMakerLink} target="_blank" rel="noopener noreferrer" className="image-maker-link">{ProjectModel.FigmaVersionControl.imageMakerName}</a></span>}</div>
            </Col>
            <Col sm={3} className="editorial-item">
              <img src={ProjectModel.FigmaSoftwareIsCultureSwag.src} alt="" style={{ display: 'block', width: '100%' }} />
              <div className="caption">{ProjectModel.FigmaSoftwareIsCultureSwag.client}</div>
              <div className="title">{ProjectModel.FigmaSoftwareIsCultureSwag.title}{ProjectModel.FigmaSoftwareIsCultureSwag.imageMakerName && <span style={{ display: 'block' }}><a href={ProjectModel.FigmaSoftwareIsCultureSwag.imageMakerLink} target="_blank" rel="noopener noreferrer" className="image-maker-link">{ProjectModel.FigmaSoftwareIsCultureSwag.imageMakerName}</a></span>}</div>
            </Col>
            <Col sm={6} className="editorial-item">
              <video src={ProjectModel.FigmaPatternLibrary.src} autoPlay muted loop playsInline style={{ display: 'block', width: '100%' }} />
              <div className="caption">{ProjectModel.FigmaPatternLibrary.client}</div>
              <div className="title">{ProjectModel.FigmaPatternLibrary.title}{ProjectModel.FigmaPatternLibrary.imageMakerName && <span style={{ display: 'block' }}><a href={ProjectModel.FigmaPatternLibrary.imageMakerLink} target="_blank" rel="noopener noreferrer" className="image-maker-link">{ProjectModel.FigmaPatternLibrary.imageMakerName}</a></span>}</div>
            </Col>
          </Row>
          </div>

          <div className="cs-section-sep">
          <Row>
            <Col sm={6} className="editorial-item">
              <img src={ProjectModel.FigmaForZines.src} alt="" style={{ display: 'block', width: '100%' }} />
              <div className="caption">{ProjectModel.FigmaForZines.client}</div>
              <div className="title">{ProjectModel.FigmaForZines.title}{ProjectModel.FigmaForZines.imageMakerName && <span style={{ display: 'block' }}><a href={ProjectModel.FigmaForZines.imageMakerLink} target="_blank" rel="noopener noreferrer" className="image-maker-link">{ProjectModel.FigmaForZines.imageMakerName}</a></span>}</div>
            </Col>
            <Col sm={3} className="editorial-item">
              <img src={ProjectModel.FigmaForksPlus.src} alt="" style={{ display: 'block', width: '100%' }} />
              <div className="caption">{ProjectModel.FigmaForksPlus.client}</div>
              <div className="title">{ProjectModel.FigmaForksPlus.title}{ProjectModel.FigmaForksPlus.imageMakerName && <span style={{ display: 'block' }}><a href={ProjectModel.FigmaForksPlus.imageMakerLink} target="_blank" rel="noopener noreferrer" className="image-maker-link">{ProjectModel.FigmaForksPlus.imageMakerName}</a></span>}</div>
            </Col>
          </Row>
          </div>

        </Container>
      </div>

    </div>
  );
}

export default connect()(Homepage);
